import { AuthContext, useBodyBottomPadding } from '@repo-level/core-front';
import useEventListener from '@repo-level/core-front/hooks/eventListener';
import { AuthButtons } from 'components/auth/AuthButtons';
import { PromoDot } from 'components/PromoDot';
import { BonusesNotificationContext } from 'contexts/bonusesNotification';
import { ModalsContext, ModalType } from 'contexts/modal';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { TabBarContext } from 'contexts/tabBarSize';
import { useLuckyWheel } from 'hooks/luckyWheel';
import { useTheme } from 'hooks/theme';
import { useCmsPageVisibility } from 'hooks/useCmsPageVisibility';
import { useAuroraCrystalAvailable } from 'modules/crystal/hooks';
import { sendSearchOverallAppearanceEvent } from 'modules/googleAnalytics/events/searchGamesEvents';
import { getSpinsLeftAmount } from 'modules/wheel/components/helpers';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'react-navi';
import { ScTabBar } from './styled';

export const TabBar = () => {
  const {
    i18n: { language },
  } = useTranslation('glossary');
  const { navigate } = useNavigation();

  const ref = useRef<HTMLDivElement>(null);

  const { isAuthenticated, user } = useContext(AuthContext);
  const { handleModal } = useContext(ModalsContext);
  const { lootboxes } = useContext(RealTimeServiceContext);
  const { hasUnseenBonuses } = useContext(BonusesNotificationContext);
  const { handleTabBarHeight } = useContext(TabBarContext);

  const { setBodyBottomPadding } = useBodyBottomPadding();
  const { menu, hamburgerIcon: HamburgerIcon } = useTheme();
  const { isExtraLootbox } = useLuckyWheel();
  const { isDateValid, isUserValid } = useAuroraCrystalAvailable();

  useEventListener('resize', () => handleTabBarHeight(ref), undefined, true);

  useEffect(() => {
    const handleScroll = () => {
      if (ref == null || ref.current == null) return;
      setBodyBottomPadding(ref.current.offsetHeight);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setBodyBottomPadding]);

  const BurgerMenuButton = useCallback(
    () => (
      <li>
        <button
          onClick={() => {
            handleModal(ModalType.TabbarMenu)();
          }}
          type="button"
        >
          <HamburgerIcon />
        </button>
      </li>
    ),
    [HamburgerIcon, handleModal]
  );

  const handleSearchClick = useCallback(() => {
    sendSearchOverallAppearanceEvent('tabbar');
    void navigate('/search-games');
  }, [navigate]);

  const isFortuneWheelVisible = useCmsPageVisibility('fortuneWheel');

  const isWheelVisible =
    isFortuneWheelVisible && (isDateValid ? isUserValid : true);
  return (
    <ScTabBar
      className={language}
      isAuthenticated={isAuthenticated}
      isWheelVisible={isWheelVisible}
      ref={ref}
    >
      <ul className="menu-list">
        {!isAuthenticated && (
          <li>
            <AuthButtons horizontal={true} />
          </li>
        )}

        <li>
          <button
            onClick={() => {
              void navigate('/');
              setTimeout(() => window.scrollTo(0, 0), 0);
            }}
            type="button"
          >
            <menu.homeIconLarge.component />
          </button>
        </li>

        {isAuthenticated && (
          <>
            <li>
              <button
                onClick={() => {
                  void navigate('/bonuses');
                }}
                type="button"
              >
                <menu.fortuneIconL.component />
                {hasUnseenBonuses && <PromoDot />}
              </button>
            </li>

            {isWheelVisible && (
              <li>
                <button
                  onClick={() => {
                    void navigate('/lucky-wheel');
                  }}
                  type="button"
                >
                  <menu.wheelIconL.component />
                  <div className="wheel-spins">
                    {isExtraLootbox ? (
                      <menu.star.component />
                    ) : (
                      getSpinsLeftAmount(lootboxes || [], user)
                    )}
                  </div>
                </button>
              </li>
            )}

            <li>
              <button onClick={handleSearchClick} type="button">
                <menu.searchIconLarge.component />
              </button>
            </li>
          </>
        )}

        <BurgerMenuButton />
      </ul>
    </ScTabBar>
  );
};
